import { FC, memo, useEffect, useRef, useState } from "react"

interface ICardDescriptionSvgProps {
  text: string
  color?: string
  scale?: number
}

const BASE_RATIO = 0.13
const LINE_HEIGHT = 1.1

const CardDescriptionSvgComponent: FC<ICardDescriptionSvgProps> = ({
  text,
  color = "#462507",
  scale = 1
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const measureRef = useRef<SVGTextElement>(null)

  const [lines, setLines] = useState<string[]>([])
  const [fontSize, setFontSize] = useState(0)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return

    const update = () => {
      const width = svg.clientWidth
      const height = svg.clientHeight
      setSize({ width, height })
    }

    const observer = new ResizeObserver(update)
    observer.observe(svg)
    update()

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const { width, height } = size
    if (!width || !height || !text) {
      setLines([])
      return
    }

    const measure = measureRef.current
    if (!measure) return

    const fs = width * BASE_RATIO * scale
    measure.setAttribute("font-size", `${fs}`)

    const maxLines = Math.floor(height / (fs * LINE_HEIGHT))
    if (maxLines <= 0) {
      setLines([])
      return
    }

    // Step 1: Word wrap into unlimited lines
    const words = text.split(" ")
    const allLines: string[] = []
    let current = ""

    for (const word of words) {
      const test = current ? `${current} ${word}` : word
      measure.textContent = test

      if (measure.getComputedTextLength() > width && current) {
        allLines.push(current)
        current = word
      } else {
        current = test
      }
    }
    if (current) allLines.push(current)

    // Step 2: Truncate to maxLines with ellipsis
    const truncated = allLines.length > maxLines
    const visible = allLines.slice(0, maxLines)

    if (truncated && visible.length > 0) {
      const lastIdx = visible.length - 1
      const lastWords = visible[lastIdx].split(" ")

      while (lastWords.length > 0) {
        measure.textContent = lastWords.join(" ") + "..."
        if (measure.getComputedTextLength() <= width) break
        lastWords.pop()
      }

      visible[lastIdx] =
        lastWords.length > 0 ? lastWords.join(" ") + "..." : "..."
    }

    // Step 3: Shrink font if any line overflows container
    let maxLineWidth = 0
    for (const line of visible) {
      measure.textContent = line
      maxLineWidth = Math.max(maxLineWidth, measure.getComputedTextLength())
    }

    const adjustedFs =
      maxLineWidth > width && width > 0 ? fs * (width / maxLineWidth) : fs

    setFontSize(adjustedFs)
    setLines(visible)
  }, [size.width, size.height, text, scale])

  if (!text) return null

  const totalHeight = lines.length * fontSize * LINE_HEIGHT
  // 0.8 ≈ ascent ratio — positions baseline correctly without dominantBaseline (iOS bug)
  const startY = (size.height - totalHeight) / 2 + fontSize * 0.8

  return (
    <svg
      ref={svgRef}
      style={{ position: "relative", zIndex: 2, overflow: "hidden" }}
    >
      <text ref={measureRef} visibility="hidden" aria-hidden="true" />
      <text textAnchor="middle" fill={color} fontSize={fontSize}>
        {lines.map((line, i) => (
          <tspan key={i} x="50%" y={startY + i * fontSize * LINE_HEIGHT}>
            {line}
          </tspan>
        ))}
      </text>
    </svg>
  )
}

export const CardDescriptionSvg = memo(CardDescriptionSvgComponent)
